import type { Components } from 'react-markdown';

import { CodeBlock } from './code-block';

/**
 * Базовый набор тегов для react-markdown под палитру algocode.io.
 *
 * Используется и в ChatMarkdown, и в TheoryMarkdown. Цвета через классы
 * Tailwind (gray-1..5, accent, green, red); темная тема — через `dark:`
 * варианты (`darkMode: ['class']` в tailwind.config.ts).
 */
export const baseComponents: Components = {
	h1: ({ children }) => (
		<h1 className="mb-5 mt-2 text-[36px] font-semibold tracking-[-0.6px] text-gray-2 dark:text-white">
			{children}
		</h1>
	),
	h2: ({ children }) => (
		<h2 className="mb-2 mt-6 text-[22px] font-semibold tracking-[-0.6px] text-gray-2 dark:text-white">
			{children}
		</h2>
	),
	h3: ({ children }) => (
		<h3 className="mb-1 mt-5 text-[18px] font-semibold tracking-[-0.3px] text-gray-2 dark:text-white">
			{children}
		</h3>
	),
	h4: ({ children }) => (
		<h4 className="mb-2 mt-5 text-[22px] font-semibold leading-tight text-gray-2 dark:text-white">
			{children}
		</h4>
	),
	h5: ({ children }) => (
		<h5 className="mb-2 mt-5 text-[22px] font-semibold leading-tight text-gray-2 dark:text-white">
			{children}
		</h5>
	),
	p: ({ children }) => (
		<p className="mt-5 font-normal leading-relaxed text-[#4B5563] dark:text-gray-4">
			{children}
		</p>
	),
	a: ({ href, children }) => (
		<a
			href={href}
			target="_blank"
			rel="noopener noreferrer ugc"
			className="text-gray-2 underline decoration-gray-4 underline-offset-2 transition-colors hover:decoration-gray-1 dark:text-white dark:decoration-gray-3 dark:hover:decoration-white"
		>
			{children}
		</a>
	),
	strong: ({ children }) => (
		<strong className="font-semibold text-gray-2 dark:text-white">
			{children}
		</strong>
	),
	em: ({ children }) => <em className="italic">{children}</em>,
	del: ({ children }) => (
		<del className="text-gray-3 line-through">{children}</del>
	),
	hr: () => <hr className="my-6 border-t border-gray-4 dark:border-gray-3" />,
	ul: ({ children }) => (
		<ul className="mt-4 list-disc font-normal leading-relaxed text-[#4B5563] dark:text-gray-4">
			{children}
		</ul>
	),
	ol: ({ children }) => (
		<ol className="mt-4 list-decimal font-normal leading-relaxed text-[#4B5563] dark:text-gray-4">
			{children}
		</ol>
	),
	li: ({ children, className }) => {
		if (className?.includes('task-list-item')) {
			return (
				<li className="ml-6 mt-2 flex list-none items-start gap-2">
					{children}
				</li>
			);
		}
		return <li className="ml-6 mt-2">{children}</li>;
	},
	blockquote: ({ children }) => (
		<blockquote className="mt-4 border-l-2 border-gray-4 pl-4 italic text-gray-3 dark:border-gray-3">
			{children}
		</blockquote>
	),
	table: ({ children }) => (
		<div className="mt-4 w-full overflow-x-auto">
			<table className="w-full border-collapse text-sm">{children}</table>
		</div>
	),
	thead: ({ children }) => <thead>{children}</thead>,
	tbody: ({ children }) => <tbody>{children}</tbody>,
	tr: ({ children }) => (
		<tr className="border-b border-gray-5 last:border-b-0 dark:border-gray-2 [thead_&]:border-gray-4 dark:[thead_&]:border-gray-3">
			{children}
		</tr>
	),
	th: ({ children }) => (
		<th className="py-2.5 pr-8 text-left font-semibold text-gray-2 last:pr-0 dark:text-white">
			{children}
		</th>
	),
	td: ({ children }) => (
		<td className="py-2.5 pr-8 text-[#4B5563] last:pr-0 dark:text-gray-4">
			{children}
		</td>
	),
	img: ({ src, alt, title, width, height }) => (
		<img
			src={typeof src === 'string' ? src : undefined}
			alt={alt ?? ''}
			title={title}
			width={width}
			height={height}
			loading="lazy"
			decoding="async"
			className="mt-4 h-auto max-w-full rounded-md"
		/>
	),
	code: CodeBlock,
	pre: ({ children }) => <>{children}</>,
};
